import React, { useEffect, useState } from 'react';
import { Moment } from 'moment';
import { momentForDomain } from '@common/services/moment-shadow';
import { useQuery, useNavigate, useCustomEvent } from '@common/hooks';
import { Calendar, Button, Row, Col, Badge } from 'antd';
import { Colors } from '@common/tokens';
import { DateNavigationTitle, Icon } from '@common/components';
import {
  LOCALSTORAGE_SHIFT_CALENDAR_FILTER_KEY,
  SHIFTS_COUNT_BY_DAY,
  ShiftCountByDayResponse,
} from '@scheduling/graphql';
import { ShiftsCalendarDateCell } from './shifts-calendar-date-cell';
import { sanitizeLength } from '@scheduling/services';
import Filters from 'works/legacy/lib/filters';
import { buildDialogUrl } from '@common/services';

const moment = momentForDomain('scheduling', 'shifts');

export function ShiftsCalendar() {
  const navigate = useNavigate();

  const localStorageFilters = Filters.getFilters(
    LOCALSTORAGE_SHIFT_CALENDAR_FILTER_KEY
  );

  const currentDate = moment();
  const [selectedDate, setSelectedDate] = useState(currentDate);
  const [selectedFilters, setSelectedFilters] = useState(localStorageFilters);

  const { data, loading } = useQuery<ShiftCountByDayResponse>(
    SHIFTS_COUNT_BY_DAY,
    {
      variables: {
        year: selectedDate.year(),
        month: selectedDate.format('MMMM'),
        filters: {
          timeOfDay: selectedFilters['timeOfDay'],
          unitIds: selectedFilters['unitIds'],
          roleIds: selectedFilters['roleIds'],
          facilityIds: selectedFilters['facilityIds'],
          length: sanitizeLength(selectedFilters['length']),
        },
      },
      fetchPolicy: 'cache-first',
    }
  );

  useEffect(() => {
    moment.updateLocale('en', {
      weekdaysMin: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    });
  }, []);

  useCustomEvent('shift-calendar-filters-updated', () => {
    setSelectedFilters(
      Filters.getFilters(LOCALSTORAGE_SHIFT_CALENDAR_FILTER_KEY)
    );
  });

  const onDaySelect = (date: Moment) => {
    const drawerUrl = buildDialogUrl('shifts-calendar-details-drawer', {
      props: { date: date.format('YYYY-MM-DD') },
    });

    navigate(drawerUrl);
  };

  const buildHeader = ({ value }: { value: Moment }) => {
    const dateLastMonth = moment(value).subtract(1, 'month');
    const dateNextMonth = moment(value).add(1, 'month');

    // Removes empty arrays to calculate active filters correctly
    for (const key in localStorageFilters) {
      if (
        Array.isArray(localStorageFilters[key]) &&
        !localStorageFilters[key].length
      )
        delete localStorageFilters[key];
    }

    const filtersLength = Object.keys(localStorageFilters).length;

    return (
      <>
        <Row
          align="middle"
          justify="center"
          style={{ marginTop: 20, marginBottom: 12 }}
        >
          <Col span={12}>
            <DateNavigationTitle
              loading={loading}
              onPrev={() => setSelectedDate(dateLastMonth)}
              onNext={() => setSelectedDate(dateNextMonth)}
            >
              {selectedDate.format('MMMM YYYY')}
            </DateNavigationTitle>
          </Col>
          <Col span={12}>
            <Row justify="end" align="middle">
              <Button
                style={{ marginLeft: 12 }}
                onClick={() =>
                  navigate(buildDialogUrl('shifts-calendar-filters-drawer'))
                }
              >
                <div>
                  <Icon icon="slidersUp" kind="light" />
                  <span style={{ margin: 8 }}>Filters</span>
                  <Badge
                    count={filtersLength}
                    style={{ backgroundColor: Colors.elmDarker, marginTop: -2 }}
                  />
                </div>
              </Button>
            </Row>
          </Col>
        </Row>
      </>
    );
  };

  return (
    <>
      <Calendar
        className="ShiftCalendar"
        headerRender={buildHeader}
        dateCellRender={(date) => (
          <ShiftsCalendarDateCell
            date={date}
            loading={loading}
            shiftsCountByDay={data?.shiftsCountByDay || []}
          />
        )}
        value={selectedDate}
        defaultValue={currentDate}
        validRange={[
          moment(selectedDate).startOf('month'),
          moment(selectedDate).endOf('month'),
        ]}
        onSelect={onDaySelect}
      />

      <style jsx>{`
        :global(
          .ShiftCalendar.ant-picker-calendar-full
            .ant-picker-panel
            .ant-picker-body
            th
        ) {
          text-align: left;
          padding: 5px 12px 5px 12px;
        }
        :global(.ShiftCalendar .ant-picker-content > thead > tr) {
          background: ${Colors.steelLightest};
        }
        :global(
          .ShiftCalendar.ant-picker-calendar-full
            .ant-picker-panel
            .ant-picker-body
            th
        ) {
          padding-top: 8px;
          padding-bottom: 8px;
        }
      `}</style>
    </>
  );
}
